import React, { useEffect, useState, useCallback } from 'react';
import type { PropsWithChildren } from 'react';

import { lockBodyScroll, unlockBodyScroll } from '../utils/bodyScrollLock';

interface ResponsiveModalBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional title (desktop header + accessible label). If omitted, ariaLabel should be provided. */
  title?: string | React.ReactNode;
  /** Bottom sticky area (e.g. action buttons). */
  stickyFooter?: React.ReactNode;
  /** Mobile sheet height utility (e.g. h-[85vh]); defaults to auto up to max 85vh. */
  heightClass?: string;
  /** Allow dismiss via overlay / close controls. */
  closable?: boolean;
  /** Override desktop width utility classes. */
  desktopWidthClass?: string;
  /** Override desktop height utility classes (e.g. md:h-[70vh]); if unset content auto-sizes with max height + internal scroll. */
  desktopHeightClass?: string;
  /** Extra classes for outer overlay. */
  overlayClassName?: string;
  /** Extra classes for desktop modal container. */
  containerClassName?: string;
  /** Optional aria-label if no title provided. */
  ariaLabel?: string;
  /** z-index utility override. */
  zIndexClass?: string;
  /** Show close icon on mobile (default true). */
  showCloseOnMobile?: boolean;
}

/**
 * ResponsiveModalBottomSheet
 * Mobile: animated bottom sheet (slides up/down) with optional sticky footer.
 * Desktop (md+): centered modal sized to intrinsic content (with max height) unless overridden via props.
 * Mirrors API conventions of ResponsiveModalPage where sensible.
 */
const ResponsiveModalBottomSheet: React.FC<
  PropsWithChildren<ResponsiveModalBottomSheetProps>
> = ({
  isOpen,
  onClose,
  children,
  title,
  stickyFooter,
  heightClass,
  closable = true,
  desktopWidthClass = 'md:w-[520px]',
  desktopHeightClass, // optional
  overlayClassName = '',
  containerClassName = '',
  ariaLabel,
  zIndexClass = 'z-60',
  showCloseOnMobile = true,
}) => {
  const [isVisible, setIsVisible] = useState(false); // controls presence (unmount after animation)
  const [shouldAnimate, setShouldAnimate] = useState(false); // controls animation state

  // Manage mount/unmount for animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Start animation after mount to ensure slide-up effect
      const timer = setTimeout(() => setShouldAnimate(true), 10);
      return () => clearTimeout(timer);
    } else {
      setShouldAnimate(false);
      const timer = setTimeout(() => setIsVisible(false), 300); // match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent background scroll (iOS-safe and supports nested modals)
  useEffect(() => {
    if (!isVisible) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isVisible]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable && isOpen) {
        onClose();
      }
    },
    [closable, isOpen, onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isVisible) return null;

  const labelled = ariaLabel || (typeof title === 'string' ? title : 'Dialog');

  return (
    <div
      className={`fixed inset-0 ${zIndexClass}`}
      role="dialog"
      aria-modal="true"
      aria-label={labelled}
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${shouldAnimate ? 'opacity-50' : 'opacity-0'} ${overlayClassName}`}
        onClick={() => {
          if (closable) onClose();
        }}
        aria-hidden="true"
      />

      {/* Desktop modal */}
      <div
        className={`md:shadow-card hidden bg-white md:absolute md:top-1/2 md:left-1/2 md:flex md:-translate-x-1/2 md:-translate-y-1/2 md:flex-col md:rounded-2xl ${desktopWidthClass} ${desktopHeightClass || 'md:h-auto md:max-h-[85vh]'} md:overflow-hidden ${containerClassName} ${shouldAnimate ? 'md:scale-100 md:opacity-100' : 'md:scale-95 md:opacity-0'} transition-all duration-300`}
      >
        {(title || closable) && (
          <div className="flex items-center justify-between border-b border-gray-200 px-5 pt-5 pb-3">
            <div className="flex-1">
              {title && (
                <h2 className="text-primary-text text-lg leading-[26px] font-semibold md:text-xl md:leading-7">
                  {title}
                </h2>
              )}
            </div>
            {closable && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                type="button"
              >
                <svg
                  className="h-6 w-6 text-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div
          data-scroll-lock-target
          className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${desktopHeightClass ? '' : 'md:pr-1'}`}
        >
          {children}
        </div>
        {stickyFooter && (
          <div className="shrink-0 border-t border-gray-100 bg-white">
            {stickyFooter}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <div
        className={`absolute right-0 bottom-0 left-0 flex transform flex-col rounded-t-[24px] bg-white pt-4 transition-transform duration-300 ease-out md:hidden ${heightClass ? heightClass : 'h-auto max-h-[85vh]'} ${shouldAnimate ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {(title || (closable && showCloseOnMobile)) && (
          <div className="flex items-center justify-between px-5 pb-2">
            <div className="flex-1">
              {title && (
                <h2 className="text-primary-text text-lg leading-[26px] font-semibold">
                  {title}
                </h2>
              )}
            </div>
            {closable && showCloseOnMobile && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                type="button"
              >
                <svg
                  className="h-6 w-6 text-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div
          data-scroll-lock-target
          className={`${heightClass ? 'min-h-0 flex-1 overflow-y-auto' : 'overflow-y-auto'} px-0`}
        >
          {children}
        </div>
        {stickyFooter && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white">
            {stickyFooter}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsiveModalBottomSheet;
